import React, { useEffect, useState } from 'react'
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogTrigger } from '../../ui/alert-dialog'
import { AlertDialogCancel } from '@radix-ui/react-alert-dialog'
import { Button } from '../../ui/button'
import { isValid } from 'date-fns'
import vault from '../../../assets/images/vault.svg';
import { Label } from '../../ui/label'
import { Input } from '../../ui/input'
import { ArrowLeft, Check, Equal, X } from 'lucide-react'
import { Tablist, TablistItem } from '../../ui/tablist'

import { useDebounce } from '@uidotdev/usehooks'
import axios from '../../../lib/axios'
import useAuth from '../../../hooks/useAuth'
import { Toaster, toast } from 'react-hot-toast'
import NewCheckbox from '../../ui/new-checkbox'
import { setCookie } from '../../../lib/cookies'
import { useTranslation } from 'react-i18next'
import { GenericCoin, GenericNote } from '../../elements/'

interface Props {
    selectedDate: Date,
    cn: Type | 'cn',
    onSave: (obj:AnnouncementKeepsafeForeignCurrency) => void;
    devies_currencies: DeviesCurrency
}


type Type = 'c' | 'n';

const NewKeepsafeForeignCurrency : React.FC<Props> = ({selectedDate, cn, onSave, devies_currencies}) => {
    const {auth} = useAuth()
    const {t} = useTranslation()

    const [coupure_input, setcoupure_input] = useState<{qty:number, coupure:number}[]>();
    const [selected_type, setselected_type] = useState<Type>((cn === 'c' || cn === 'cn') ? 'c' : 'n');
    const [barcode, setbarcode] = useState<string>('');
    const [barcode_error, setbarcode_error] = useState<'correct' | StateType>(null);

    const debouncedBarcode = useDebounce(barcode, 300)
    

    useEffect(()=>{
        if(debouncedBarcode !== '' && debouncedBarcode.substring(0,2) === 'BE') {
            const checkBarcode = async () => {
                try {
                    setbarcode_error('loading')
                    const { data } = await axios.post(`barcode/check`, {
                        token: auth.data?.token,
                        barcode: debouncedBarcode
                    })
                    if(data.errorcode === 0){
                        if(data.object.barcode.valid === true && data.object.barcode.remark === ''){
                            setbarcode_error('correct')
                        } else {
                            setbarcode_error('error')
                        }
                    }
                    else {
                        setbarcode_error('error')
                    }
                } catch (error) {
                    setbarcode_error('error')
                    console.log(error)
                }
            }
            checkBarcode()
        }
        else {
            setbarcode_error(null)
        }
    }, [debouncedBarcode])

    const onChangeInput = (coupure:number, qty:number) => {
        qty = Math.round(qty)
        if(coupure_input) {
            let tmp = [...coupure_input]
            const existingIndex = coupure_input.findIndex(item => item.coupure === coupure);
            
            if(existingIndex !== -1){
                tmp[existingIndex] = {qty:qty, coupure:coupure}
            }
            else {
                tmp.push({qty:qty, coupure:coupure})
            }
            setcoupure_input(tmp);
        }
        else {
            setcoupure_input([{coupure:coupure, qty:qty}])
        }
    };

    const calculateSubtotal = (coupure:number) => {
        let total = 0;

        if(coupure_input){
            const input = coupure_input.find(i=>i.coupure === coupure)
            if(input){
                total = input.coupure * input?.qty
            }       
        }

        return Math.round(total * 100)/100 || 0
    }

    const calculateTotal = (coupures:{qty:number, coupure:number}[] | undefined, cn:'c' | 'n') => {
        let total = 0;
    
        if(coupures) {
            let tmp = [...coupures]

            const c_devies = devies_currencies.devies.filter(i=>i.cn.toLowerCase() === 'c')
            const n_devies = devies_currencies.devies.filter(i=>i.cn.toLowerCase() === 'n')

            if(cn === 'c'){
                tmp = tmp.filter(i => c_devies.findIndex(d => d.value === i.coupure) > -1)
            } else {
                tmp = tmp.filter(i => n_devies.findIndex(d => d.value === i.coupure) > -1)
            }            
    
            tmp.forEach(item => {
                if(!isNaN(item.qty) && !isNaN(item.coupure)){
                    total += item.qty * item.coupure
                }
            });
        }
    
        return total;
    }

    const CoinsInput = () => {
        return (
            <div className='flex flex-col gap-2'>
                {devies_currencies.devies.filter(i=>i.cn.toLowerCase() === 'c').sort((a, b) => b.value - a.value).map((devies) => (
                    <div className='grid grid-cols-[auto_auto_auto_auto_1fr] items-center gap-2' key={`coin-devies-${devies.value}`}>
                        <Input onBlur={({currentTarget})=>{ onChangeInput(devies.value, parseFloat(currentTarget.value)) }} defaultValue={coupure_input?.find(i => i.coupure === devies.value)?.qty ?? 0} className='w-20' type='number' min={0}/>
                        <span className='text-sm text-muted-foreground'>×</span>
                        <GenericCoin value={devies.value} currency={devies_currencies.deviescurrencyname || 'EUR'} className='w-8 h-8'/>
                        <Equal className='w-5' strokeWidth={1}/>
                        <p className='font-bold text-right'>{calculateSubtotal(devies.value)} {devies_currencies.deviescurrencyname}</p>
                    </div>
                ))}
   
                <div className='flex justify-end py-2 text-sm border-t'>
                    <p>{t("components.o_announcement.o_newkeepsafe.total")}: <span className='font-medium'>{calculateTotal(coupure_input, 'c')} {devies_currencies.deviescurrencyname}</span></p> 
                </div>
            </div>
        )
    }

    const NotesInput = () => {
        return (
            <div className='flex flex-col gap-2'>

                {devies_currencies.devies.filter(i=>i.cn.toLowerCase() === 'n').sort((a, b) => b.value - a.value).map((devies) => (
                    <div className='grid grid-cols-[auto_auto_auto_auto_1fr] items-center gap-2' key={`coin-devies-${devies.value}`}>
                        <Input onBlur={({currentTarget})=>{ onChangeInput(devies.value, parseFloat(currentTarget.value)) }} defaultValue={coupure_input?.find(i => i.coupure === devies.value)?.qty ?? 0} className='w-20' type='number' min={0}/>
                        <span className='text-sm text-muted-foreground'>×</span>
                        <GenericNote value={devies.value} currency={devies_currencies.deviescurrencyname || 'EUR'} className='w-16 h-8'/>
                        <Equal className='w-5' strokeWidth={1}/>
                        <p className='font-bold text-right whitespace-nowrap'>{calculateSubtotal(devies.value)} {devies_currencies.deviescurrencyname}</p>
                    </div>
                ))}

                <div className='flex justify-end py-2 text-sm border-t'>
                    <p>{t("components.o_announcement.o_newkeepsafe.total")}: <span className='font-medium'>{calculateTotal(coupure_input, 'n')} {devies_currencies.deviescurrencyname}</span></p> 
                </div>
            </div>
        )
    }

    const saveKeepsafe = () => {
        let object:AnnouncementKeepsafeForeignCurrency = {keepsafe:barcode, detail:[]}

        if(object.keepsafe === ''){
            toast.error(t("components.o_announcement.o_newkeepsafe.toast_error"))
            return;
        }
        if(coupure_input){
            //Keepsafe aangemaakt
            
            // @ts-ignore
            object.detail.push({bag:barcode, coupures:coupure_input})

            const c_devies = devies_currencies.devies.filter(i=>i.cn.toLowerCase() === 'c')
            const n_devies = devies_currencies.devies.filter(i=>i.cn.toLowerCase() === 'n')

            if(selected_type === 'c'){
                //Keep all coins
                object.detail[0].coupures = object.detail[0].coupures.filter(i => c_devies.findIndex(d => d.value === i.coupure) > -1)
            }
            else {
                //Keep all notes
                object.detail[0].coupures = object.detail[0].coupures.filter(i => n_devies.findIndex(d => d.value === i.coupure) > -1)
            }
        }
        else {
            //Moederkeepsafe aanmaken
        }
        onSave(object)
        setcoupure_input(undefined)
        setbarcode('')
    }

    type MethodKeepsafe = 'manual' | 'amount'
    
    const [method_keepsafe, setmethod_keepsafe] = useState<MethodKeepsafe | null>('manual');
    const [remember_method, setremember_method] = useState<boolean>(true);

    const saveMethod = (method:MethodKeepsafe) => {
        if(remember_method){
            setCookie('methodKeepsafe', method)
        }
        setmethod_keepsafe(method)
    }

  return (
    <>
    <Toaster />
    <AlertDialog>
        <AlertDialogTrigger asChild>
            <Button variant='dark' disabled={!(isValid(selectedDate))} className='flex gap-2 mt-4 w-fit'><img className='h-6' src={vault} alt="vault"/> {t("components.o_announcement.o_newkeepsafe.button_new_keepsafe")}</Button>
        </AlertDialogTrigger>
        <AlertDialogContent className='max-h-[95vh] overflow-y-auto'>
            {method_keepsafe === null &&
            <div className='flex flex-col gap-4'>
                <div className='flex items-center justify-between'>
                    <h2 className='text-xl font-medium'>{t("components.o_announcement.o_newkeepsafe.keepsafe_question")}</h2>
                    <AlertDialogCancel asChild><button className='opacity-50 hover:opacity-100'><X /></button></AlertDialogCancel>
                </div>
                <div className='flex items-center gap-2'>
                    <NewCheckbox checked={remember_method} onCheckedChange={(v: boolean)=>{setremember_method(v)}} id='remember_ks'/>
                    <Label htmlFor='remember_ks'>{t("components.o_announcement.o_newkeepsafe.remember")}</Label>
                </div>
                <div className='flex gap-2'>
                    <Button onClick={()=>{saveMethod('manual')}}>{t("components.o_announcement.o_newkeepsafe.manual")}</Button>
                    <Button onClick={()=>{saveMethod('amount')}} variant='outline'>{t("components.o_announcement.o_newkeepsafe.loose")}</Button>
                </div>
            </div>
            }

            {method_keepsafe === 'manual' &&
            <>
            <div className='flex items-center justify-between'>
                <div className='relative overflow-hidden group'>
                    <div className='flex items-center gap-1 duration-100'>
                        <h2 className='text-xl font-medium'>{t("components.o_announcement.o_newkeepsafe.title")}</h2>
                    </div>
                </div>
                <AlertDialogCancel asChild><button className='opacity-50 hover:opacity-100'><X /></button></AlertDialogCancel>
            </div>

            <div>
                <Label htmlFor='barcode'>{t("components.o_announcement.o_newkeepsafe.label_barcode")}<span className='ml-1 text-primary'>*</span></Label>
                <Input endEnhancer={
                <>
                    {barcode_error === 'correct' && <div className='flex items-center justify-center w-5 h-5 bg-green-400 rounded-full'><Check className='w-3 text-white'/></div>}
                    {barcode_error === 'error' && <div className='flex items-center justify-center w-5 h-5 bg-red-400 rounded-full'><X className='w-3 text-white'/></div>}
                </>} onChange={({currentTarget})=>setbarcode(currentTarget.value)} id='barcode'/>
                {barcode_error === 'error' && <p className='text-sm text-red-600'>{t("components.o_announcement.o_newkeepsafe.barcode_error")}</p>}
            </div>
            
            {cn === 'cn' && 
            <Tablist value={selected_type} onValueChange={(v)=>{setselected_type(v as Type)}}>
                <TablistItem value='c'>{t("components.o_announcement.o_newkeepsafe.coins")}</TablistItem>
                <TablistItem value='n'>{t("components.o_announcement.o_newkeepsafe.notes")}</TablistItem>
            </Tablist>
            }

            {selected_type === 'c' && <CoinsInput />}
            {selected_type === 'n' && <NotesInput />}

            <div>
                <AlertDialogAction disabled={barcode === ''} onClick={()=>{saveKeepsafe()}}>{t("components.o_announcement.o_newkeepsafe.button_save_keepsafe")}</AlertDialogAction>
                <Button onClick={()=>{setcoupure_input(undefined)}} variant='ghost'>{t("components.o_announcement.o_newkeepsafe.button_clear_form")}</Button>
            </div>
            </>
            }            
        </AlertDialogContent>
    </AlertDialog>
    </>
  )
}

export default NewKeepsafeForeignCurrency
